import React, { FC, useEffect, useState } from 'react';
import { CircularProgress, Container, Grid, TextField, Typography } from '@material-ui/core';
import Card from 'src/components/Card';
import { createUseStyles } from 'src/theming';
import styles from './Site.styles';
import { Controller, useForm } from 'react-hook-form';
import Site, { CreateSiteOptions } from 'src/interfaces/api/Site';
import GMaps from './components/GMaps';
import Button from '../../../../components/Button';

const useStyles = createUseStyles(styles);

interface Props {
  site: Site;
  onChange: (createSiteOptions: CreateSiteOptions) => Promise<Site>;
}

const SiteView: FC<Props> = ({ site, onChange }) => {
  const classes = useStyles();
  const [formError, setFormError] = useState<string>();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<CreateSiteOptions>({ defaultValues: site });

  useEffect(() => {
    reset(site);
  }, [site]);

  const onSubmit = async (createSiteOptions: CreateSiteOptions) => {
    try {
      setFormError(undefined);
      await onChange(createSiteOptions);
    } catch (e) {
      setFormError(e.message);
      throw e;
    }
  };

  return (
    <Container className={classes.root} maxWidth='lg'>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <Typography className={classes.title} variant='h5'>
              Site d'installation
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name='socialReason'
                  control={control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Raison sociale'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='siret'
                  control={control}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Siret'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={7}>
                <Controller
                  name='address'
                  control={control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Adresse'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <Controller
                  name='postalCode'
                  control={control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Code postal'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <Controller
                  name='city'
                  control={control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Ville'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name='phoneNumber'
                  control={control}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Téléphone'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name='clientCode'
                  control={control}
                  render={({ field: { value, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Code client'
                      variant='outlined'
                      value={value || ''}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              {formError && <p className={classes.error}>{formError}</p>}

              <Grid item xs={12} style={{ textAlign: 'end' }}>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant='contained'
                  color='primary'
                  type='submit'
                  disabled={!isDirty || isSubmitting}
                >
                  {isSubmitting && <CircularProgress color='primary' size={15} />} Enregistrer
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <Typography className={classes.title} variant='h5'>
              Emplacement
            </Typography>

            <GMaps address={`${site.address}, ${site.postalCode} ${site.city}`} />
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SiteView;
